import React, { useEffect, useMemo, useRef, useState } from "react";
import * as bootstrap from "bootstrap";
import fivePaisaWebSocketService from "../services/fivePaisaWebSocketService";
import angelOneWebSocketService, {
  getAngelExchangeTypeFromHolding,
} from "../services/angelOneWebSocketService";
import growwLivePriceService from "../services/growwLivePriceService";
import "../css/setTargetModal.css";

const GROW_POLL_SOURCE = "setTargetModal";

const initialFormState = {
  targetAmount: "",
  targetDays: "",
  targetReason: "",
  targetReasonDetails: "",
};

const normalizeExchange = (exchange) => {
  if (exchange === "N") return "NSE";
  if (exchange === "B") return "BSE";
  return exchange || "";
};

const normalizeExchangeType = (exchangeType) => {
  if (exchangeType === "C") return "Cash";
  if (exchangeType === "D") return "Derivatives";
  return exchangeType || "";
};

const toMarketFeedExchange = (exchange) => {
  const raw = String(exchange || "").toUpperCase();
  if (raw.startsWith("B")) return "B";
  if (raw.startsWith("M")) return "M";
  return "N";
};

const toMarketFeedExchangeType = (exchangeType) => {
  const raw = String(exchangeType || "").toUpperCase();
  return raw.startsWith("D") ? "D" : "C";
};

const buildGrowPollingKey = (stock = {}) => {
  const symbol = String(stock?.symbol || stock?.Symbol || stock?.name || "")
    .trim()
    .toUpperCase();
  if (!symbol) return "";
  const exchangePrefix = toMarketFeedExchange(stock?.exch || stock?.Exch) === "B"
    ? "BSE"
    : toMarketFeedExchange(stock?.exch || stock?.Exch) === "M"
      ? "MCX"
      : "NSE";
  return `${exchangePrefix}_${symbol}`;
};

// const cleanupModalArtifacts = () => {
//   document.querySelectorAll(".modal-backdrop").forEach((backdrop) => {
//     backdrop.remove();
//   });

//   document.body.classList.remove("modal-open");
//   document.body.style.removeProperty("overflow");
//   document.body.style.removeProperty("padding-right");
// };

function SetTargetModal({
  modalId = "SetTargetModal",
  stock,
  tr,
  formatNumber,
  apiBaseUrl,
  source = "watchlist",
  sourceWatchlist,
  userId,
  brokerType,
  onSuccess,
  showSuccessToast,
  showErrorToast,
  showWarningToast,
}) {
  const [form, setForm] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [liveCurrentPrice, setLiveCurrentPrice] = useState(null);
  const [livePreviousClose, setLivePreviousClose] = useState(null);
  const growPriceUnsubRef = useRef(null);
  const fivePaisaPriceUnsubRef = useRef(null);
  const fivePaisaSubscriptionRef = useRef(null);
  const angelOnePriceUnsubRef = useRef(null);
  const angelOneSubscriptionRef = useRef(null);

  useEffect(() => {
    setForm(initialFormState);
  }, [stock]);

  useEffect(() => {
    setLiveCurrentPrice(
      Number.isFinite(Number(stock?.modalCurrentPrice))
        ? Number(stock?.modalCurrentPrice)
        : Number.isFinite(Number(stock?.CurrentPrice))
          ? Number(stock?.CurrentPrice)
          : Number.isFinite(Number(stock?.["Current Price"]))
            ? Number(stock?.["Current Price"])
            : null,
    );
    setLivePreviousClose(
      Number.isFinite(Number(stock?.PreviousClose))
        ? Number(stock?.PreviousClose)
        : Number.isFinite(Number(stock?.PClose))
          ? Number(stock?.PClose)
          : Number.isFinite(Number(stock?.modalPreviousClose))
            ? Number(stock?.modalPreviousClose)
            : null,
    );
  }, [stock]);

  useEffect(() => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) return undefined;

    const handleHidden = () => {
      setForm(initialFormState);
      setIsSubmitting(false);
      // cleanupModalArtifacts();
    };

    modalElement.addEventListener("hidden.bs.modal", handleHidden);
    return () => {
      modalElement.removeEventListener("hidden.bs.modal", handleHidden);
    };
  }, [modalId]);

  useEffect(() => {
    const clearGrowPolling = () => {
      if (growPriceUnsubRef.current) {
        growPriceUnsubRef.current();
        growPriceUnsubRef.current = null;
      }
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
    };
    const clearFivePaisaSubscription = () => {
      if (fivePaisaPriceUnsubRef.current) {
        fivePaisaPriceUnsubRef.current();
        fivePaisaPriceUnsubRef.current = null;
      }
      if (fivePaisaSubscriptionRef.current) {
        fivePaisaWebSocketService.unsubscribeMarketFeed([
          fivePaisaSubscriptionRef.current,
        ]);
        fivePaisaSubscriptionRef.current = null;
      }
    };
    const clearAngelOneSubscription = () => {
      if (angelOnePriceUnsubRef.current) {
        angelOnePriceUnsubRef.current();
        angelOnePriceUnsubRef.current = null;
      }
      if (angelOneSubscriptionRef.current) {
        angelOneWebSocketService.unsubscribeMarketFeed([
          angelOneSubscriptionRef.current,
        ]);
        angelOneSubscriptionRef.current = null;
      }
    };

    clearGrowPolling();
    clearFivePaisaSubscription();
    clearAngelOneSubscription();

    if (!stock) return () => {};

    const broker = String(brokerType || stock?.broker_type || "")
      .trim()
      .toLowerCase();

    if (broker === "5paisa") {
      const scripCode = Number(stock?.ScripCode || stock?.scripcode || stock?.scripCode || 0);
      if (!scripCode) return () => {};

      const subItem = {
        Exch: toMarketFeedExchange(stock?.Exch || stock?.exch),
        ExchType: toMarketFeedExchangeType(stock?.ExchType || stock?.exchType),
        ScripCode: scripCode,
      };
      fivePaisaSubscriptionRef.current = subItem;

      fivePaisaPriceUnsubRef.current = fivePaisaWebSocketService.onPrice((tick) => {
        if (!tick) return;
        const token = Number(tick?.Token);
        if (!token || token !== scripCode) return;
        const ltp = Number(tick?.LastRate);
        const pClose = Number(tick?.PClose);
        if (Number.isFinite(ltp)) setLiveCurrentPrice(ltp);
        if (Number.isFinite(pClose)) setLivePreviousClose(pClose);
      });

      fivePaisaWebSocketService.connect();
      fivePaisaWebSocketService.subscribeMarketFeed([subItem]);
    } else if (broker === "angelone") {
      const token = String(
        stock?.token || stock?.Token || stock?.scripcode || stock?.script_code || "",
      ).trim();
      if (!token) return () => {};

      const subItem = {
        exchangeType: getAngelExchangeTypeFromHolding(stock),
        token,
      };
      angelOneSubscriptionRef.current = subItem;

      angelOnePriceUnsubRef.current = angelOneWebSocketService.onPrice((tick) => {
        if (!tick) return;
        const tickToken = String(tick?.Token || "").trim();
        if (!tickToken || tickToken !== token) return;
        const ltp = Number(tick?.LastRate);
        const pClose = Number(tick?.PClose);
        if (Number.isFinite(ltp)) setLiveCurrentPrice(ltp);
        if (Number.isFinite(pClose) && pClose > 0) setLivePreviousClose(pClose);
      });

      angelOneWebSocketService.connect();
      angelOneWebSocketService.subscribeMarketFeed([subItem]);
    } else if (broker === "grow") {
      const growKey = buildGrowPollingKey(stock);
      if (!growKey) return () => {};

      growwLivePriceService.setSymbols(GROW_POLL_SOURCE, [growKey]);
      growPriceUnsubRef.current = growwLivePriceService.subscribe((payload) => {
        const ltp = Number(payload?.[growKey]);
        if (Number.isFinite(ltp)) setLiveCurrentPrice(ltp);
      });
      growwLivePriceService.startPolling(2000);
    }

    return () => {
      clearGrowPolling();
      clearFivePaisaSubscription();
      clearAngelOneSubscription();
    };
  }, [stock, brokerType]);

  const resolvedCurrentPrice = useMemo(
    () =>
      parseFloat(
        liveCurrentPrice ??
          stock?.modalCurrentPrice ??
          stock?.currentPrice ??
          stock?.CurrentPrice ??
          0,
      ),
    [liveCurrentPrice, stock],
  );
  const resolvedPreviousClose = useMemo(
    () =>
      parseFloat(
        livePreviousClose ??
          stock?.modalPreviousClose ??
          stock?.PreviousClose ??
          stock?.PClose ??
          stock?.pclose ??
          stock?.["Current Price"] ??
          0,
      ),
    [livePreviousClose, stock],
  );
  const resolvedInitialPrice = useMemo(
    () =>
      parseFloat(
        liveCurrentPrice ??
          stock?.CurrentPrice ??
          stock?.currentPrice ??
          stock?.modalCurrentPrice ??
          stock?.initialPrice ??
          0,
      ),
    [liveCurrentPrice, stock],
  );
  const stockName =
    stock?.InstrumentName ||
    stock?.FullName ||
    stock?.fullName ||
    stock?.name ||
    stock?.Name ||
    "";

  const validateForm = () => {
    if (!form.targetAmount) {
      showErrorToast("Please enter target amount");
      return false;
    }

    if (!form.targetDays) {
      showErrorToast("Please enter target days");
      return false;
    }

    if (!form.targetReason.trim()) {
      showErrorToast("Please enter reason");
      return false;
    }

    return true;
  };

  const hideModal = () => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) return;

    const modalInstance =
      bootstrap.Modal.getInstance(modalElement) ||
      bootstrap.Modal.getOrCreateInstance(modalElement);
    modalInstance.hide();
    // window.setTimeout(cleanupModalArtifacts, 300);
  };

  const handleSubmit = async () => {
    if (!stock) {
      showWarningToast("Please select a stock first");
      return;
    }

    if (!validateForm()) return;

    const targetPrice = parseFloat(form.targetAmount);
    const targetDays = parseInt(form.targetDays, 10);

    if (isNaN(targetPrice) || targetPrice <= 0) {
      showErrorToast("Please enter valid target amount");
      return;
    }

    if (isNaN(targetDays) || targetDays <= 0) {
      showErrorToast("Please enter valid target days");
      return;
    }

    const requestBody = {
      Exch: normalizeExchange(stock?.exch || stock?.Exch),
      ExchType: normalizeExchangeType(stock?.exchType || stock?.ExchType),
      initialPrice: !isNaN(resolvedInitialPrice) ? resolvedInitialPrice : 0,
      name: stockName,
      scripCode: String(
        stock?.token || stock?.Token || stock?.scripcode || stock?.ScripCode || "",
      ),
      source,
      source_watchlist: sourceWatchlist,
      symbol:
        stock?.Symbol ||
        stock?.symbol ||
        stock?.TradingSymbol ||
        stock?.Name ||
        "",
      targetDays,
      targetPrice,
      targetReason: form.targetReason.trim(),
      targetReasonDetails: form.targetReasonDetails.trim(),
      target_logic:
        resolvedInitialPrice > targetPrice ? "lower_target" : "higher_target",
      userId: Number(userId) || 0,
      broker_type: brokerType || "",
    };

    try {
      setIsSubmitting(true);
      console.log("targettttt", requestBody);

      const response = await fetch(`${apiBaseUrl}/api/v1/stock-target/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      const responseData = result?.data;

      if (!response.ok || responseData?.status !== "success") {
        showErrorToast(
          responseData?.message || result?.message || "Failed to add stock target",
        );
        return;
      }

      showSuccessToast(responseData.message || "Stock target added successfully");
      setForm(initialFormState);
      hideModal();
      onSuccess?.(responseData);
    } catch (error) {
      console.error("Error adding stock target:", error);
      showErrorToast("Something went wrong while adding the stock target");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasValidPrices =
    !isNaN(resolvedCurrentPrice) &&
    !isNaN(resolvedPreviousClose) &&
    resolvedCurrentPrice > 0 &&
    resolvedPreviousClose > 0;
  const priceDifference = hasValidPrices
    ? resolvedCurrentPrice - resolvedPreviousClose
    : 0;
  const changePercent = hasValidPrices
    ? ((priceDifference / resolvedPreviousClose) * 100).toFixed(2)
    : "0.00";
  const isPositive = priceDifference >= 0;
  const priceUp = resolvedCurrentPrice >= resolvedPreviousClose;
  const canSubmit =
    !!form.targetAmount && !!form.targetDays && !!form.targetReason.trim() && !isSubmitting;

  return (
    <div
      className="modal fade stm-modal"
      id={modalId}
      aria-labelledby={`${modalId}Label`}
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-md">
        <div className="modal-content border-0">
          <header className="stm-header">
            <button
              type="button"
              className="btn-close stm-header__close"
              data-bs-dismiss="modal"
              aria-label={tr("close")}
            />
            <h2 className="stm-header__title" id={`${modalId}Label`}>
              {tr("setTarget")}
            </h2>
            <p className="stm-header__subtitle">{stockName}</p>
          </header>

          <div className="stm-body-wrap">
            <div className="stm-card">
              <div className="stm-quote">
                <div>
                  <span className="stm-quote__label">{tr("currentPrice")}</span>
                  <div
                    className={`stm-quote__price ${
                      priceUp ? "stm-quote__price--up" : "stm-quote__price--down"
                    }`}
                  >
                    ₹{formatNumber(resolvedCurrentPrice)}
                  </div>
                </div>
                <div className="stm-quote__side">
                  <span className="stm-quote__label">{tr("netChange")}</span>
                  <div
                    className={`stm-quote__change ${
                      isPositive
                        ? "stm-quote__change--up"
                        : "stm-quote__change--down"
                    }`}
                  >
                    {isPositive ? "+" : "-"}₹
                    {Math.abs(priceDifference).toFixed(2)}
                    <span className="stm-quote__pct">
                      ({isPositive ? "+" : ""}
                      {changePercent}%)
                    </span>
                  </div>
                </div>
              </div>

              <div className="stm-divider" aria-hidden />

              <div className="stm-grid-2">
                <div className="stm-field">
                  <label className="stm-label" htmlFor={`${modalId}-target-amount`}>
                    {tr("targetPrice")}
                  </label>
                  <div className="stm-input-wrap stm-input-wrap--rupee">
                    <span className="stm-input-prefix" aria-hidden>
                      ₹
                    </span>
                    <input
                      id={`${modalId}-target-amount`}
                      type="number"
                      className="stm-input"
                      placeholder={tr("zeroAmountPlaceholder")}
                      min="0"
                      value={form.targetAmount}
                      onKeyDown={(e) => {
                        if (
                          e.key === "-" ||
                          e.key === "e" ||
                          e.key === "E" ||
                          e.key === "+"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || parseFloat(value) >= 0) {
                          setForm((prev) => ({
                            ...prev,
                            targetAmount: value,
                          }));
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="stm-field">
                  <label className="stm-label" htmlFor={`${modalId}-target-days`}>
                    {tr("duration")}
                  </label>
                  <div className="stm-input-wrap stm-input-wrap--days">
                    <input
                      id={`${modalId}-target-days`}
                      type="number"
                      className="stm-input"
                      placeholder={tr("zeroPlaceholder")}
                      min="0"
                      value={form.targetDays}
                      onKeyDown={(e) => {
                        if (
                          e.key === "-" ||
                          e.key === "e" ||
                          e.key === "E" ||
                          e.key === "+" ||
                          e.key === "."
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value === "" ||
                          (parseFloat(value) >= 0 &&
                            Number.isInteger(parseFloat(value)))
                        ) {
                          setForm((prev) => ({
                            ...prev,
                            targetDays: value,
                          }));
                        }
                      }}
                    />
                    <span className="stm-suffix">{tr("days")}</span>
                  </div>
                </div>
              </div>

              <div className="stm-field stm-field--mt">
                <label className="stm-label" htmlFor={`${modalId}-reason`}>
                  {tr("reason")}
                  <span className="stm-req" aria-hidden>
                    *
                  </span>
                </label>
                <input
                  id={`${modalId}-reason`}
                  type="text"
                  className="stm-input"
                  list={`${modalId}-reason-suggestions`}
                  placeholder={tr("reasonPlaceholder")}
                  value={form.targetReason}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      targetReason: e.target.value,
                    }));
                  }}
                />
                <datalist id={`${modalId}-reason-suggestions`}>
                  <option value="BUY" />
                  <option value="SELL" />
                  <option value="HOLD" />
                  <option value="EXIT" />
                  <option value="PROFIT" />
                  <option value="ACCUMULATE" />
                  <option value="REBALANCE" />
                  <option value="SWING" />
                </datalist>
              </div>

              <div className="stm-field stm-field--mt">
                <label className="stm-label" htmlFor={`${modalId}-note`}>
                  {tr("additionalNoteOptional")}
                </label>
                <textarea
                  id={`${modalId}-note`}
                  className="stm-input"
                  placeholder={tr("additionalNotePlaceholder")}
                  rows={3}
                  value={form.targetReasonDetails}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      targetReasonDetails: e.target.value,
                    }));
                  }}
                />
              </div>
            </div>
          </div>

          <footer className="stm-footer">
            <button
              type="button"
              className="stm-btn stm-btn--secondary"
              data-bs-dismiss="modal"
            >
              {tr("cancel")}
            </button>
            <button
              type="button"
              className="stm-btn stm-btn--primary"
              onClick={() => {
                if (!canSubmit) {
                  showWarningToast(tr("fillTargetAmountDaysReason"));
                  return;
                }
                handleSubmit();
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? tr("loading") || "Submitting..." : tr("confirmTarget")}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default SetTargetModal;
